import { fetcher, getValidToken } from "../utils/fetcher";
import type {
  ActivityLogEntry,
  CreateActivityLogEntry,
  UpdateActivityLogEntry,
  SearchVolunteer,
} from "../types/evidence";

export const activityLogService = {
  /**
   * Get all activity log entries for an organisation with optional filters
   */
  getActivityLog: async (
    organisationId: string,
    filters?: {
      volunteer_name?: string;
      volunteer_id?: string;
      volunteer_email?: string;
      activity_description?: string;
      note?: string;
      created_by?: string;
      date_from?: string;
      date_to?: string;
    },
  ): Promise<ActivityLogEntry[]> => {
    const params = new URLSearchParams({ organisation_id: organisationId });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }

    return fetcher<ActivityLogEntry[]>({
      url: `/activity-log?${params.toString()}`,
    });
  },

  /**
   * Create a new activity log entry
   */
  createActivityLog: async (
    organisationId: string,
    entry: CreateActivityLogEntry,
  ): Promise<ActivityLogEntry> => {
    return fetcher<ActivityLogEntry>({
      url: `/activity-log?organisation_id=${organisationId}`,
      method: "POST",
      body: JSON.stringify(entry),
    });
  },

  /**
   * Update an existing activity log entry
   */
  updateActivityLog: async (
    organisationId: string,
    activityLogId: string,
    entry: UpdateActivityLogEntry,
  ): Promise<ActivityLogEntry> => {
    return fetcher<ActivityLogEntry>({
      url: `/activity-log/${activityLogId}?organisation_id=${organisationId}`,
      method: "PUT",
      body: JSON.stringify(entry),
    });
  },

  /**
   * Delete an activity log entry
   */
  deleteActivityLog: async (
    organisationId: string,
    activityLogId: string,
  ): Promise<void> => {
    return fetcher<void>({
      url: `/activity-log/${activityLogId}?organisation_id=${organisationId}`,
      method: "DELETE",
    });
  },

  /**
   * Export activity log as CSV
   */
  exportActivityLog: async (
    organisationId: string,
    filters?: {
      volunteer_name?: string;
      volunteer_id?: string;
      volunteer_email?: string;
      activity_description?: string;
      note?: string;
      created_by?: string;
      date_from?: string;
      date_to?: string;
    },
  ): Promise<Blob> => {
    const params = new URLSearchParams({ organisation_id: organisationId });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
    }

    const token = await getValidToken();
    const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
    const response = await fetch(
      `${apiBase}/activity-log/export?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    return response.blob();
  },

  /**
   * Search volunteers for autocomplete (minimum 3 characters)
   */
  searchVolunteers: async (query: string): Promise<SearchVolunteer[]> => {
    if (query.length < 3) {
      return [];
    }

    return fetcher<SearchVolunteer[]>({
      url: `/activity-log/volunteers/search?q=${encodeURIComponent(query)}`,
    });
  },
};
